import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FileText, PoundSterling } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import OrderChip from '../Brand/OrderChip';
import { SkeletonCards } from '../Brand/BrandLoading';
import EmptyState from '../ui/empty-state';
import { ukDate, money } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
];

export const MyPay = ({ token }) => {
  const [period, setPeriod] = useState('this_month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff/my-pay?period=${period}`, { headers });
      setData(res.data);
    } catch (err) {
      toast.error('Could not load your pay');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const downloadStatement = async () => {
    try {
      const res = await axios.get(`${API}/staff/my-pay/statement?period=${period}`, { headers, responseType: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(res.data);
      link.download = `butler-direct-statement-${period}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error('No statement available for that period');
    }
  };

  const pay = data?.pay;

  return (
    <div className="space-y-4" data-testid="my-pay">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`my-pay-period-${p.key}`}
            className={`min-h-10 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
              period === p.key ? 'bg-gold text-ink border-gold'
                : 'bg-white text-slate-600 border-hair hover:border-gold hover:bg-cream'
            }`}
          >
            {p.label}
          </button>
        ))}
        <Button variant="outline" size="sm" className="ml-auto" onClick={downloadStatement} data-testid="my-pay-pdf">
          <FileText className="w-4 h-4 mr-2" /> My statement (PDF)
        </Button>
      </div>

      {loading && <SkeletonCards count={3} testId="my-pay-loading" />}

      {!loading && pay && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Card className="bd-card-accent bg-white"><CardContent className="p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Owed to you</p>
              <p className="mt-1 text-2xl font-black tabular-nums text-ink" data-testid="my-pay-owed">{money(pay.owed_total)}</p>
            </CardContent></Card>
            <Card className="bg-white"><CardContent className="p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Paid</p>
              <p className="mt-1 text-2xl font-black tabular-nums text-green-700" data-testid="my-pay-paid">{money(pay.paid_total)}</p>
            </CardContent></Card>
          </div>

          {pay.lines.length === 0 && pay.adjustments.length === 0 && (
            <EmptyState
              icon={PoundSterling}
              title="No pay yet"
              line="Your pay appears here once a job is completed."
              testId="my-pay-empty"
            />
          )}

          {pay.lines.map((line) => (
            <Card key={line.id} className="bg-white" data-testid={`my-pay-line-${line.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-ink">{line.job_type}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{ukDate(line.date)}</span>
                      <OrderChip value={line.order_number} fallback="" />
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 mt-2">
                      <span>Wage {money(line.wage)}</span>
                      <span>Travel {money(line.travel)}</span>
                      <span>Bonus {money(line.bonus)}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="bd-money font-bold text-ink">{money(line.total)}</p>
                    {line.status === 'paid' ? (
                      <Badge className="bg-green-100 text-green-800 text-xs mt-1">
                        Paid {ukDate(line.payment?.paid_date)}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 text-xs mt-1">Owed</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {pay.adjustments.map((adj) => (
            <Card key={adj.id} className="bg-slate-50" data-testid={`my-pay-adjustment-${adj.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">Adjustment</p>
                  <p className="text-xs text-slate-500">{ukDate(adj.date)} · {adj.note}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-900">{money(adj.amount)}</p>
                  <Badge className={`text-xs mt-1 ${adj.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                    {adj.status === 'paid' ? `Paid ${ukDate(adj.payment?.paid_date)}` : 'Owed'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}

          <p className="text-xs text-slate-400 flex items-center gap-1">
            <PoundSterling className="w-3 h-3" /> Travel money is worked out from the driving distance between your home postcode and the event, less the free miles allowance.
          </p>
        </>
      )}
    </div>
  );
};

export default MyPay;
